/**
 * Delta local dos contadores da inbox (abas Minhas / Escalado / IA / Todas + Abertas/Fechadas).
 *
 * Espelha exatamente a RPC `get_conversation_inbox_filter_counts`
 * (migration 20260819200717):
 *  - `open` / `closed` contam o conjunto **visível** (sem recorte por status);
 *  - `all`, `mine`, `attention`, `ia`, `unread` contam o **attention_scope**
 *    (visível ∩ statusFilter);
 *  - `unread` é por conversa: 0 → >0 = +1, >0 → 0 = −1, 1 → 2 = 0.
 *
 * Se o snapshot realtime não tiver todos os campos necessários, devolve
 * `confident: false` e o chamador deve refazer a RPC em vez de aplicar o delta.
 */
import {
  isConversationOpen,
  isConversationVisibleToMember,
  isIaActiveConversation,
  isMineConversation,
  isAttentionConversation,
  matchesStatusFilter,
  type ConversationFilterCounts,
  type ConversationStatusFilter,
  type ConversationsVisibility,
  type MemberIntegrationAccessContext,
  type FilterableConversation,
} from "@/lib/conversation-filters";

export type InboxDeltaCounts = Pick<
  ConversationFilterCounts,
  "mine" | "attention" | "ia" | "all" | "open" | "closed" | "unread"
>;

export type InboxDeltaEventType = "insert" | "update" | "delete";

export interface InboxDeltaEvent {
  type: InboxDeltaEventType;
  /** Estado anterior (update/delete). */
  before?: FilterableConversation | null;
  /** Estado novo (insert/update). */
  after?: FilterableConversation | null;
}

export interface InboxDeltaOptions {
  statusFilter: ConversationStatusFilter | null;
  currentMemberId?: string | null;
  isAdmin: boolean;
  conversationsVisibility: ConversationsVisibility;
  integrationAccess?: MemberIntegrationAccessContext | null;
}

export interface InboxDeltaResult {
  counts: InboxDeltaCounts;
  confident: boolean;
}

/** Todos os campos usados pelo cálculo precisam estar presentes no snapshot. */
export function hasCompleteFilterSnapshot(
  conv: FilterableConversation | null | undefined,
): conv is FilterableConversation {
  if (!conv) return false;
  if (typeof conv.state !== "string") return false;
  if (typeof conv.is_closed !== "boolean") return false;
  if (conv.escalated_by_agent_at === undefined) return false;
  if (typeof conv.isUnread !== "boolean") return false;
  if (conv.assigned_member_ids === undefined && conv.assigned_member_id === undefined) {
    return false;
  }
  return true;
}

function contribution(
  conv: FilterableConversation | null | undefined,
  opts: InboxDeltaOptions,
): InboxDeltaCounts {
  const zero: InboxDeltaCounts = {
    mine: 0,
    attention: 0,
    ia: 0,
    all: 0,
    open: 0,
    closed: 0,
    unread: 0,
  };
  if (!conv) return zero;

  const visible = isConversationVisibleToMember(conv, {
    isAdmin: opts.isAdmin,
    currentMemberId: opts.currentMemberId,
    conversationsVisibility: opts.conversationsVisibility,
    integrationAccess: opts.integrationAccess,
  });
  if (!visible) return zero;

  const open = isConversationOpen(conv);
  const inScope = matchesStatusFilter(conv, opts.statusFilter);

  return {
    open: open ? 1 : 0,
    closed: open ? 0 : 1,
    all: inScope ? 1 : 0,
    mine: inScope && isMineConversation(conv, opts.currentMemberId) ? 1 : 0,
    attention: inScope && isAttentionConversation(conv) ? 1 : 0,
    ia: inScope && isIaActiveConversation(conv) ? 1 : 0,
    unread: inScope && conv.isUnread ? 1 : 0,
  };
}

const COUNT_KEYS: (keyof InboxDeltaCounts)[] = [
  "mine",
  "attention",
  "ia",
  "all",
  "open",
  "closed",
  "unread",
];

export function applyInboxFilterCountDelta(
  prev: InboxDeltaCounts,
  event: InboxDeltaEvent,
  opts: InboxDeltaOptions,
): InboxDeltaResult {
  const needsBefore = event.type === "update" || event.type === "delete";
  const needsAfter = event.type === "update" || event.type === "insert";

  if (needsBefore && !hasCompleteFilterSnapshot(event.before)) {
    return { counts: prev, confident: false };
  }
  if (needsAfter && !hasCompleteFilterSnapshot(event.after)) {
    return { counts: prev, confident: false };
  }

  const before = needsBefore ? contribution(event.before, opts) : null;
  const after = needsAfter ? contribution(event.after, opts) : null;

  const counts = { ...prev };
  for (const key of COUNT_KEYS) {
    const delta = (after?.[key] ?? 0) - (before?.[key] ?? 0);
    if (delta !== 0) counts[key] = Math.max(0, counts[key] + delta);
  }

  return { counts, confident: true };
}
